import {
  Col,
  Form,
  Typography,
  Collapse,
  Row,
  theme,
  Radio,
  Checkbox,
  Table,
  Tag,
  Spin,
} from "antd";
import { useCallback, useEffect, useState } from "react";
import {
  deleteAnswer,
  getQuestionnaireAnswerResults,
} from "../../repository/data.repository";
import InModal from "../../controls/InModal";
import BandTag from "../../controls/BandTag";
import { modalTitle } from "../../controls/InModal.default";
import ModalFooter from "../../controls/ModalFooter";
import ModalDeleteConfirmation from "../../controls/ModalDeleteConfirmation";
import ModalErrorAlert from "../../controls/ModalErrorAlert";
import { getProblemMessage } from "../../utils/http";
import {
  Answer,
  QuestionnaireAnswerKPIResult,
  MAX_PAGE_SIZE,
} from "../../repository/data.default";
import { QueryOperation } from "../../code/search-query/search-query";
import { AnswerStatus, answerStatusOf, InitialAnswerValues } from "./AnswersView.default";
import { AnswerQuestionValues } from "./AnswersView.default";
import { fetchQuestionnaireAnswerData } from "./AnswersView.default";
import dayjs from "dayjs";

const { Panel } = Collapse;

export interface AnswerFormDialogProps {
  readonly open?: boolean;
  readonly initialValues: Answer;
  readonly mode: "view" | "delete";
  readonly onCancel?: () => void;
  readonly onFinish?: () => void;
}

function AnswerFormDialog(props: AnswerFormDialogProps) {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("Something went wrong, try again");
  const [kpiResults, setKpiResults] = useState<QuestionnaireAnswerKPIResult[]>([]);

  useEffect(() => {
    if (props.mode === 'view' && props.open && props.initialValues.questionnaireUid) {
      setLoading(true);
      setError(false);
      setKpiResults([]);
      // Clear any prior answer's fields immediately so the modal never flashes stale content
      // before the new data resolves (the body is covered by a spinner while loading).
      form.resetFields();
      fetchQuestionnaireAnswerData(
        props.initialValues.questionnaireUid,
        props.initialValues.uid
      )
        .then((formData) => {
          const formValues : InitialAnswerValues = {
            userUserName: props.initialValues.userUserName,
            createdOn: dayjs(props.initialValues.createdOn).format("DD/MM/YYYY HH:mm"),
            completedAt: props.initialValues.completedAt
              ? dayjs(props.initialValues.completedAt).format("DD/MM/YYYY HH:mm")
              : "—",
            status: AnswerStatus[answerStatusOf(props.initialValues)],
            bundleName: props.initialValues.bundleName,
            questionnaireName: props.initialValues.questionnaireName,
            companyName: props.initialValues.companyName,
            ...formData,
          };
          form.setFieldsValue(formValues);
        })
        .catch((error) => {
          setErrorMessage(getProblemMessage(error));
          setError(true);
        })
        .finally(() => {
          setLoading(false);
        });

      // KPI results are computed asynchronously by the scoring engine — fetch
      // them independently so a not-yet-scored answer still renders cleanly.
      getQuestionnaireAnswerResults({
        searchQuery: {
          conditions: [
            {
              field: "questionnaireAnswerUid",
              operation: QueryOperation.Equal,
              values: [props.initialValues.uid],
            },
          ],
        },
        pageNumber: 0,
        pageSize: MAX_PAGE_SIZE,
        sortDescending: false,
      })
        .then(({ data }) => setKpiResults(data))
        .catch((error) => {
          console.error("Error loading answer KPI results:", error);
        });
    }
  }, [props.open, props.mode, props.initialValues])

  // Events

  function onSubmit(): void {
    setLoading(true);
    setError(false);
    deleteAnswer(props.initialValues.uid)
      .then(() => {
        setLoading(false);
        props.onFinish?.();
      })
      .catch((e: any) => {
        setErrorMessage(getProblemMessage(e));
        setLoading(false);
        setError(true);
      });
  }

  function onCancel(): void {
    props.onCancel?.();
  }

  // methods

  const renderAnswer = useCallback((questionValues: AnswerQuestionValues) => {
    switch (questionValues.questionType) {
      case "SingleChoice":
        return (
          <Radio.Group
            options={questionValues.choices}
            defaultValue={questionValues.answers![0]}
            className="read-only"
          />
        );
      case "MultipleChoice":
        return (
          <Checkbox.Group
            options={questionValues.choices}
            defaultValue={questionValues.answers}
            className="read-only"
          />
        );
      case "FreeText":
        return <Typography.Text>{questionValues.answerText}</Typography.Text>;
      case "Numeric":
        return (
          <Typography.Text>
            {questionValues.numericAnswer ?? "—"}
            {questionValues.numericAnswer != null && questionValues.unit
              ? ` ${questionValues.unit}`
              : ""}
          </Typography.Text>
        );
    }
  }, []);

  return (
    <InModal
      open={props.open}
      onCancel={onCancel}
      title={modalTitle(
        props.mode,
        "Answer",
        props.initialValues?.questionnaireName,
      )}
      actions={
        props.mode === "delete" && (
          <ModalFooter
            mode={props.mode}
            loading={loading}
            onCancel={onCancel}
            onSubmit={onSubmit}
          />
        )
      }
      size={props.mode === 'view' ? "extraLarge" : "small"}

    >
      {props.mode === 'view' && (
        <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          className="my-form-item flex flex-col pb-4"
        >
          <Row className="flex flex-auto flex-row gap-10">
            <Form.Item 
              name={'userUserName'}
              key={'userUserName'} 
              className="m-0 p-1"
              valuePropName="children"
              label={
                  <Typography.Text
                    className="text-sm"
                    style={{
                      color: token.colorTextDescription,
                    }}
                  >
                    User Name
                  </Typography.Text>
                }
              >
                <Typography.Text />
              </Form.Item>
              <Form.Item 
                name={'questionnaireName'}
                key={'answer-questionnaire'} 
                className="m-0 p-1"
                valuePropName="children"
                label={
                  <Typography.Text
                    className="text-sm"
                    style={{
                      color: token.colorTextDescription,
                    }}
                  >
                    Questionnaire
                  </Typography.Text>
                }
              >
                <Typography.Text />
              </Form.Item>
              <Form.Item 
                name={'bundleName'}
                key={'answer-bundle'} 
                className="m-0 p-1"
                valuePropName="children"
                label={
                  <Typography.Text
                    className="text-sm"
                    style={{
                      color: token.colorTextDescription,
                    }}
                  >
                    Bundle
                  </Typography.Text>
                }
              >
                <Typography.Text />
              </Form.Item>
              <Form.Item 
                name={'companyName'}
                key={'answer-company'} 
                className="m-0 p-1"
                valuePropName="children"
                label={
                  <Typography.Text
                    className="text-sm"
                    style={{
                      color: token.colorTextDescription,
                    }}
                  >
                    Company
                  </Typography.Text>
                }
              >
                <Typography.Text />
              </Form.Item>
              <Form.Item 
                name={'createdOn'}
                key={'answer-createdOn'} 
                className="m-0 p-1"
                valuePropName="children"
                label={
                  <Typography.Text
                    className="text-sm"
                    style={{
                      color: token.colorTextDescription,
                    }}
                  >
                    Started On
                  </Typography.Text>
                }
              >
                <Typography.Text />
              </Form.Item>
              {/* The moment the answer's results are reported against. */}
              <Form.Item
                name={'completedAt'}
                key={'answer-completedAt'}
                className="m-0 p-1"
                valuePropName="children"
                label={
                  <Typography.Text
                    className="text-sm"
                    style={{
                      color: token.colorTextDescription,
                    }}
                  >
                    Completed On
                  </Typography.Text>
                }
              >
                <Typography.Text />
              </Form.Item>
              <Form.Item
                name={'status'}
                key={'answer-status'}
                className="m-0 p-1"
                valuePropName="children"
                label={
                  <Typography.Text
                    className="text-sm"
                    style={{
                      color: token.colorTextDescription,
                    }}
                  >
                    Status
                  </Typography.Text>
                }
              >
                <Typography.Text />
              </Form.Item>
          </Row>
          <Form.List name="sections">
              {(sections) => (
                <Col className="flex flex-col p-1 mt-5">
                  {sections.map((section, sectionIndex) => (
                    <Collapse
                      key={section.key}
                      className="my-collapse flex flex-col"
                      bordered={false}
                      accordion
                    >
                      <Panel
                        key={section.key}
                        header={`Section ${sectionIndex + 1} - ${form.getFieldValue(['sections', sectionIndex]).name}`}
                      >
                        <Col className="flex flex-col gap-2">
                          <Form.Item
                            key={`form-item-desc-${section.key}`}
                            name={[section.name, "description"]}
                            valuePropName="children"
                            label={
                              <Typography.Text
                                className="text-sm"
                                style={{
                                  color: token.colorTextDescription,
                                }}
                              >
                                Description
                              </Typography.Text>
                            }
                            className="m-0 p-1"
                          >
                            <Typography.Text />
                          </Form.Item>

                          {/* Questions */}
                          <Typography.Text
                            className="text-sm"
                            style={{
                              color: token.colorTextDescription,
                              marginTop: "0.5rem",
                              paddingLeft: "0.5rem",
                            }}
                          >
                            Questions
                          </Typography.Text>
                          <Form.List name={[section.name, "questions"]}>
                            {(
                              questions,
                            ) => (
                              <Col className="flex flex-col p-1">
                                <Collapse
                                  className="my-collapse flex flex-col"
                                  bordered={false}
                                  defaultActiveKey={['0']}
                                >
                                  {questions.map((question, questionIndex) => {
                                    const questionValues = form.getFieldValue([
                                      "sections", 
                                      sectionIndex, 
                                      "questions", 
                                      questionIndex,
                                    ]) || {};
                                  return(
                                    <Panel
                                      key={question.key}
                                      header={
                                        <>
                                          {`Question ${questionIndex + 1} - ${questionValues.description}`}
                                          {questionValues.isRequired && (
                                            <Tag color="red" className="ml-2">
                                              Required
                                            </Tag>
                                          )}
                                        </>
                                      }
                                    >
                                      <Col className="flex flex-col">
                                        {/* hide answeredAt temporarily */}
                                        {/*<Form.Item
                                          name={[question.name, "answeredAt"]}
                                          className="mb-[10px]"
                                        >
                                          <Typography.Text>Answered at {dayjs(questionValues.answeredAt).format("DD/MM/YYYY HH:mm")}</Typography.Text>
                                        </Form.Item>*/}

                                        {/* Choices */}
                                        {renderAnswer(questionValues)}
                                      </Col>
                                    </Panel>
                                  )})}
                                </Collapse>
                              </Col>
                            )}
                          </Form.List>
                        </Col>
                      </Panel>
                    </Collapse>
                  ))}
                </Col>
              )}
            </Form.List>
        </Form>

        <Col className="flex flex-col p-1 mt-5">
          <Typography.Text
            className="text-sm"
            style={{
              color: token.colorTextDescription,
              paddingLeft: "0.5rem",
            }}
          >
            Assessment Results
          </Typography.Text>
          {kpiResults.length ? (
            <Table<QuestionnaireAnswerKPIResult>
              className="mt-2"
              size="small"
              rowKey="uid"
              pagination={false}
              dataSource={kpiResults}
              columns={[
                {
                  title: "KPI",
                  dataIndex: "kpiDefinitionName",
                  key: "kpiDefinitionName",
                },
                {
                  title: "Score",
                  dataIndex: "score",
                  key: "score",
                  width: 120,
                  render: (value: number) =>
                    value != null ? value.toFixed(2) : "-",
                },
                {
                  title: "Band",
                  dataIndex: "resultingRangeLabel",
                  key: "resultingRangeLabel",
                  width: 180,
                  render: (label?: string, record?: QuestionnaireAnswerKPIResult) => (
                    <BandTag label={label} color={record?.resultingRangeColor} />
                  ),
                },
              ]}
            />
          ) : (
            <Typography.Text type="secondary" className="mt-2 pl-2">
              Not yet scored
            </Typography.Text>
          )}
        </Col>
        </Spin>
      )}
      {props.mode === 'delete' && (
        <ModalDeleteConfirmation entityLabel="answer" />
      )}

      <ModalErrorAlert
        message={error ? errorMessage : undefined}
      />
    </InModal>
  );
}

export default AnswerFormDialog;
